import { t } from "@lingui/macro";
import { atom, useAtomValue, useSetAtom } from "jotai";
import { useCallback } from "react";
import { ScrollView } from "react-native";

import { Button } from "@whoosh/design-system/src/components/controls";
import {
  Box,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { ProceedToInitialStepAtom } from "../../../../atoms/actions";
import { VerificationModalStepStateAtom } from "../../../../atoms/stores";

const AccessYourAccountDataAtom = atom((get) => {
  const state = get(VerificationModalStepStateAtom);
  if (state.step === "AccessYourAccount") {
    return {
      emailOrPhone: state.emailOrPhone,
    };
  }

  return {};
});

export const AccessYourAccountStep = () => {
  const verificationStepData = useAtomValue(AccessYourAccountDataAtom);
  const { emailOrPhone } = verificationStepData ?? {};
  const goToBackToSignIn = useSetAtom(ProceedToInitialStepAtom);
  const handleBackToSignIn = useCallback(() => {
    goToBackToSignIn();
  }, [goToBackToSignIn]);

  return (
    <ScrollView showsVerticalScrollIndicator={false}>
      <VStack paddingHorizontal={6} width="100%">
        <VStack marginBottom={8}>
          <Text
            variant={"mainTitle2"}
            marginBottom={4}
          >{t`Access Your Account`}</Text>
          <VStack marginBottom={4}>
            <Text
              variant={"mainBaseMedium"}
              color="text-primary"
            >{t`We couldn’t find an existing account for:`}</Text>
            <Text variant="mainBaseSemiBold" color="text-primary">
              {emailOrPhone}
            </Text>
          </VStack>
          <Text variant="mainBaseMedium">
            {t`If your club instructed you to activate your account, please make sure you’ve done the following.`}
          </Text>
        </VStack>
        <VStack marginBottom={8}>
          <Box
            bg="surface-secondary"
            padding={2}
            borderRadius="2"
            width={34}
            height={34}
            justifyContent="center"
            alignItems="center"
            marginBottom={3}
          >
            <Text variant="mainBaseSemiBold">1</Text>
          </Box>
          <Text
            marginBottom={2}
            variant="mainHeading"
          >{t`Use correct email or phone number.`}</Text>
          <Text
            variant={"mainBaseMedium"}
            marginBottom={4}
          >{t`Make sure you’re using the email or phone number you provided to your club.`}</Text>
          <Button
            variant="outline"
            size="tiny"
            accessibilityLabel={t`Back to Sign In`}
            testID="back-to-sign-in-button"
            onPress={handleBackToSignIn}
          >{t`Back to Sign In`}</Button>
        </VStack>

        <VStack marginBottom={8}>
          <Box
            bg="surface-secondary"
            padding={2}
            borderRadius="2"
            width={34}
            height={34}
            justifyContent="center"
            alignItems="center"
            marginBottom={3}
          >
            <Text variant="mainBaseSemiBold">2</Text>
          </Box>
          <Text
            marginBottom={2}
            variant="mainHeading"
          >{t`Check your email for activation email`}</Text>
          <Text
            variant={"mainBaseMedium"}
          >{t`Your club should send you an email with a link to activate your account. Be sure to check your spam folder, too.`}</Text>
        </VStack>

        <VStack>
          <Box
            bg="surface-secondary"
            padding={2}
            borderRadius="2"
            width={34}
            height={34}
            justifyContent="center"
            alignItems="center"
            marginBottom={3}
          >
            <Text variant="mainBaseSemiBold">3</Text>
          </Box>
          <Text marginBottom={2} variant="mainHeading">
            {t`Contact your club directly`}
          </Text>
          <Text
            variant={"mainBaseMedium"}
          >{t`If none of the above helped, contact your club directly. They can resend your activation email.`}</Text>
        </VStack>
      </VStack>
    </ScrollView>
  );
};
